import React, { useState } from "react";
import { Play, Pause, SkipBack, SkipForward, Shuffle, Repeat, Repeat1, Volume2, VolumeX, Flame, Mic2, ListMusic, ChevronUp } from "lucide-react";
import { motion, useMotionValue, animate } from "framer-motion";
import { usePlayer } from "@/contexts/PlayerContext";
import { useAuth } from "@/contexts/AuthContext";
import { formatTime, mediumArt } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import LyricsPanel from "@/components/LyricsPanel";
import QueuePanel from "@/components/QueuePanel";
import NowPlayingView from "@/components/NowPlayingView";
import SmartImage from "@/components/SmartImage";

/**
 * BottomPlayer (MiniPlayer) — owns the shared NowPlaying ``sheetProgress``
 * motion value (0 = closed, 1 = fully open).
 *
 * Apple-Music-style coupled drag: as the user swipes up from the MiniPlayer
 * we map the upward delta into ``sheetProgress`` so the Now Playing sheet
 * tracks the finger continuously. On pointer release we use position +
 * velocity to decide whether to commit (animate to 1) or revert (to 0).
 * Tapping the MiniPlayer also animates to 1 with the same spring.
 */
const SHEET_SPRING = { type: "spring", stiffness: 300, damping: 32, mass: 0.85 };

const BottomPlayer = () => {
  const {
    current, playing, progress, duration, volume, queue,
    togglePlay, next, prev, seek, setVolume,
    shuffle, setShuffle, repeat, setRepeat, toggleLike, isLiked,
  } = usePlayer();
  const { user, login } = useAuth();
  const [muted, setMuted] = useState(false);
  const [lastVol, setLastVol] = useState(0.8);
  const [showLyrics, setShowLyrics] = useState(false);
  const [showQueue, setShowQueue] = useState(false);

  // ── Now Playing sheet drag controller ───────────────────────────────
  // Shared motion value with NowPlayingView — drives the sheet's y
  // translate continuously during the drag. 0 = MiniPlayer (closed),
  // 1 = full sheet (open). Tap-to-open + drag-to-open + drag-to-close
  // all flow through this single value.
  const sheetProgress = useMotionValue(0);
  const openSheet = () => animate(sheetProgress, 1, SHEET_SPRING);
  const closeSheet = () => animate(sheetProgress, 0, SHEET_SPRING);

  const hasTrack = !!current;
  const liked = current ? isLiked(current.trackId) : false;

  const handleLike = async () => {
    if (!current) return;
    if (!user) {
      toast.message("Sign in to save songs", { action: { label: "Sign in", onClick: login } });
      return;
    }
    await toggleLike(current);
  };

  const toggleMute = () => {
    if (muted) {
      setVolume(lastVol || 0.8);
      setMuted(false);
    } else {
      setLastVol(volume);
      setVolume(0);
      setMuted(true);
    }
  };

  const cycleRepeat = () => {
    setRepeat(repeat === "off" ? "all" : repeat === "all" ? "one" : "off");
  };

  const pct = duration > 0 ? (progress / duration) * 100 : 0;

  // iTunes 30-second preview detection — true when the only available
  // source is the 30s clip from Apple (YouTube full-length match wasn't
  // found, or track is licensed-preview-only). Transparency for the
  // listener: they see WHY this song's bar ends so quickly.
  const isPreviewOnly =
    hasTrack &&
    duration > 0 &&
    duration <= 32000 &&
    !!current.previewUrl &&
    !current._fullTrack &&
    current._source !== "audius";

  return (
    <motion.div
      data-testid="bottom-player"
      className="fixed bottom-3 sm:bottom-6 left-1/2 -translate-x-1/2 w-[96%] sm:w-[95%] max-w-5xl h-[78px] sm:h-[82px] z-50 bg-black/70 backdrop-blur-2xl border border-white/10 rounded-2xl flex items-center px-3 sm:px-5 shadow-[0_18px_50px_-12px_rgba(0,0,0,0.7)] touch-pan-x"
      // Coupled swipe-up gesture: as the user drags upward we feed the
      // delta into the shared ``sheetProgress`` motion value so the
      // NowPlaying sheet tracks the finger continuously. The MiniPlayer
      // itself stays put visually (dragElastic 0 on top, snaps back on
      // release).
      drag="y"
      dragConstraints={{ top: 0, bottom: 0 }}
      dragElastic={0}
      dragMomentum={false}
      dragDirectionLock
      onDrag={(_, info) => {
        if (info.offset.y >= 0) return;           // ignore downward drag
        const winH = window.innerHeight || 800;
        const reach = winH * 0.55;                // 55% of viewport = full open
        const p = Math.min(1, Math.abs(info.offset.y) / reach);
        sheetProgress.set(p);
      }}
      onDragEnd={(_, info) => {
        const p = sheetProgress.get();
        // Position + velocity decision (matches the NowPlayingView
        // dismiss logic so both ends feel consistent).
        if (info.velocity.y < -400 || p > 0.4) openSheet();
        else closeSheet();
      }}
    >
      {/* Left: track info — tap to open Now Playing on mobile */}
      <div className="flex items-center gap-3 w-1/4 min-w-0">
        {hasTrack ? (
          <>
            <button
              data-testid="player-open-now-playing"
              onClick={openSheet}
              className="flex items-center gap-3 min-w-0 flex-1 hover:bg-white/5 sm:hover:bg-transparent active:bg-white/10 sm:active:bg-transparent rounded-lg p-1 -m-1 transition text-left"
              type="button"
              title="Open Now Playing"
            >
              <SmartImage
                src={mediumArt(current.artworkUrl100)}
                alt={current.trackName || ""}
                seed={current.trackName || current.artistName || "?"}
                className="w-14 h-14 rounded-md overflow-hidden shadow-lg shrink-0"
                imgClassName="w-full h-full object-cover"
              />
              <div className="min-w-0 hidden sm:block">
                <p className="text-sm font-medium truncate" data-testid="player-track-name">{current.trackName}</p>
                <p className="text-xs text-white/50 truncate flex items-center gap-1.5">
                  <span className="truncate">{current.artistName}</span>
                  {isPreviewOnly && (
                    <span
                      data-testid="player-preview-badge"
                      title="30-second preview only — full track not available on this catalog"
                      className="shrink-0 text-[9px] uppercase tracking-wider font-bold px-1.5 py-[1px] rounded bg-[#E2FF31]/15 text-[#E2FF31] border border-[#E2FF31]/30"
                    >
                      Preview
                    </span>
                  )}
                </p>
              </div>
              <div className="min-w-0 flex-1 sm:hidden">
                <p className="text-sm font-medium truncate flex items-center gap-1.5">
                  <span className="truncate">{current.trackName}</span>
                  {isPreviewOnly && (
                    <span className="shrink-0 text-[8px] uppercase tracking-wider font-bold px-1 py-[1px] rounded bg-[#E2FF31]/15 text-[#E2FF31] border border-[#E2FF31]/30">
                      30s
                    </span>
                  )}
                </p>
                <p className="text-xs text-white/50 truncate">{current.artistName}</p>
              </div>
              <ChevronUp size={14} className="text-white/40 sm:hidden shrink-0" />
            </button>
            <button
              data-testid="player-like-btn"
              onClick={handleLike}
              className="ml-1 p-2 rounded-full hover:bg-white/10 transition hidden sm:block"
              title={liked ? "Remove from liked" : "Save to liked"}
            >
              <Flame size={16} fill={liked ? "#FF2E00" : "none"} stroke={liked ? "#FF2E00" : "currentColor"} />
            </button>
          </>
        ) : (
          <div className="text-sm text-white/40">Pick a song to start</div>
        )}
      </div>

      {/* Center: controls */}
      <div className="flex-1 flex flex-col items-center gap-1.5 max-w-2xl mx-auto">
        <div className="flex items-center gap-4">
          <button
            data-testid="player-shuffle-btn"
            onClick={() => setShuffle(!shuffle)}
            className={`hidden sm:block p-1.5 transition ${shuffle ? "text-[#FF2E00]" : "text-white/60 hover:text-white"}`}
            title="Shuffle"
          >
            <Shuffle size={18} />
          </button>
          <button
            data-testid="player-prev-btn"
            onClick={prev}
            disabled={!hasTrack}
            className="p-1.5 text-white/80 hover:text-white disabled:opacity-30 transition"
          >
            <SkipBack size={22} fill="currentColor" />
          </button>
          <button
            data-testid="player-play-btn"
            onClick={togglePlay}
            disabled={!hasTrack}
            className="w-10 h-10 rounded-full bg-white text-black flex items-center justify-center hover:scale-105 transition disabled:opacity-30"
          >
            {playing ? <Pause size={20} fill="currentColor" /> : <Play size={20} fill="currentColor" className="ml-0.5" />}
          </button>
          <button
            data-testid="player-next-btn"
            onClick={next}
            disabled={!hasTrack}
            className="p-1.5 text-white/80 hover:text-white disabled:opacity-30 transition"
          >
            <SkipForward size={22} fill="currentColor" />
          </button>
          <button
            data-testid="player-repeat-btn"
            onClick={cycleRepeat}
            className={`hidden sm:block p-1.5 transition ${repeat !== "off" ? "text-[#FF2E00]" : "text-white/60 hover:text-white"}`}
            title={`Repeat: ${repeat}`}
          >
            {repeat === "one" ? <Repeat1 size={18} /> : <Repeat size={18} />}
          </button>
        </div>

        <div className="flex items-center gap-2 w-full px-2">
          <span className="text-[11px] text-white/40 font-mono tabular-nums w-9 text-right">{formatTime(progress)}</span>
          <div
            data-testid="player-progress"
            className="flex-1 group h-1 bg-white/15 rounded-full cursor-pointer relative"
            onClick={(e) => {
              if (!duration) return;
              const r = e.currentTarget.getBoundingClientRect();
              const ratio = (e.clientX - r.left) / r.width;
              seek(ratio * duration);
            }}
          >
            <div
              className="absolute left-0 top-0 h-full bg-white group-hover:bg-[#FF2E00] rounded-full transition-colors"
              style={{ width: `${pct}%` }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ left: `calc(${pct}% - 6px)` }}
            />
          </div>
          <span className="text-[11px] text-white/40 font-mono tabular-nums w-9">{formatTime(duration)}</span>
        </div>
      </div>

      {/* Right: queue + lyrics + volume */}
      <div className="w-1/4 flex items-center justify-end gap-2">
        <button
          data-testid="player-queue-btn"
          onClick={() => setShowQueue(true)}
          className="p-2 text-white/60 hover:text-white transition hidden sm:block relative"
          title="Queue"
        >
          <ListMusic size={18} />
          {queue && queue.length > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#FF2E00] text-[9px] font-bold text-white flex items-center justify-center">
              {queue.length > 99 ? "99+" : queue.length}
            </span>
          )}
        </button>
        <button
          data-testid="player-lyrics-btn"
          onClick={() => setShowLyrics(true)}
          disabled={!hasTrack}
          className="p-2 text-white/60 hover:text-white disabled:opacity-30 transition hidden sm:block"
          title="Lyrics"
        >
          <Mic2 size={18} />
        </button>
        <button onClick={toggleMute} className="p-2 text-white/60 hover:text-white" data-testid="player-mute-btn">
          {muted || volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>
        <input
          data-testid="player-volume"
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={muted ? 0 : volume}
          onChange={(e) => {
            const v = parseFloat(e.target.value);
            setVolume(v);
            setMuted(v === 0);
          }}
          className="o-slider w-20 sm:w-28"
        />
      </div>

      <LyricsPanel open={showLyrics} onClose={() => setShowLyrics(false)} />
      <QueuePanel open={showQueue} onClose={() => setShowQueue(false)} />
      <NowPlayingView
        progress={sheetProgress}
        onOpen={openSheet}
        onClose={closeSheet}
        onOpenLyrics={() => { closeSheet(); setShowLyrics(true); }}
        onOpenQueue={() => { closeSheet(); setShowQueue(true); }}
      />
    </motion.div>
  );
};

export default BottomPlayer;
